import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { isAuthValidationError } from '../schemas';
import { AuthAlert } from './auth-alert';
import { AuthButton } from './auth-button';
import { FloatingInput } from './floating-input';
import { TermsCheckbox } from './terms-checkbox';

const COMPONENT = 'features:auth.components.invite_register_form' satisfies I18nPrefix;
const COMMON = 'features:auth.components.common' satisfies I18nPrefix;

type InviteRegisterFormValues = {
  email: string;
  name: string;
  password: string;
};

type InviteRegisterFormProps = {
  handlers: DashboardHandlers;
  email: string;
};

export function InviteRegisterForm({ handlers, email }: InviteRegisterFormProps) {
  const { apiHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);

  const form = useAppForm({
    defaultValues: {
      email,
      name: '',
      password: '',
    } satisfies InviteRegisterFormValues,
    onSubmit: async ({ value }) => {
      setBannerMessages([]);
      setSuccess(null);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });

        await apiHandler.fetch({
          method: 'POST',
          path: '/register',
          payload: {
            email: value.email,
            name: value.name,
            password: value.password,
          },
        });

        setSuccess(t(`${COMPONENT}.success`));
      } catch (err) {
        if (isAuthValidationError(err)) {
          const messages = [...(err.data.message ? [err.data.message] : [])];
          for (const fieldMessages of Object.values(err.data.errors)) {
            messages.push(...fieldMessages);
          }
          setBannerMessages(Array.from(new Set(messages)));
        } else {
          setBannerMessages([t(`${COMMON}.generic_error`)]);
        }
      }
      setLoading(false);
    },
  });

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      {success && (
        <AuthAlert type="success">
          <p className="text-sm">{success}</p>
        </AuthAlert>
      )}
      {bannerMessages.length > 0 && (
        <AuthAlert type="warning">
          {bannerMessages.map((message) => (
            <p key={message} className="text-sm">
              {message}
            </p>
          ))}
        </AuthAlert>
      )}

      <form.Field name="email">
        {(field) => (
          <FloatingInput
            name="email"
            type="email"
            label={t(`${COMPONENT}.email_label`)}
            autoComplete="email"
            required
            disabled
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <form.Field name="name">
        {(field) => (
          <FloatingInput
            name="name"
            label={t(`${COMPONENT}.name_label`)}
            autoComplete="name"
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <form.Field name="password">
        {(field) => (
          <FloatingInput
            name="password"
            type="password"
            label={t(`${COMPONENT}.password_label`)}
            minLength={12}
            maxLength={128}
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <TermsCheckbox
        agreePrefix={t(`${COMPONENT}.agree_prefix`)}
        linkText={t(`${COMPONENT}.terms_of_service`)}
        linkHref="https://sparklayer.io/terms"
      />

      <div>
        <AuthButton loading={loading}>{t(`${COMPONENT}.submit`)}</AuthButton>
      </div>
    </form>
  );
}
